import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { MAX_QTY, QTY_OPTIONS, snapQty } from "./boxUtils";

export default function QuantityPicker({ qty, setQty, quoteHref = "/quote" }) {
  const [text, setText] = useState(String(qty));
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const listRef = useRef(null);
  useEffect(() => setText(String(qty)), [qty]);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "center" });
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const commit = () => { const q = snapQty(text); setQty(q); setText(String(q)); };
  const sliderIdx = QTY_OPTIONS.indexOf(qty);

  return (
    <div data-testid="box-quantity-picker">
      <div className="flex items-center gap-3">
        <input type="range" min={0} max={QTY_OPTIONS.length - 1} value={sliderIdx < 0 ? 0 : sliderIdx}
          onChange={(e) => setQty(QTY_OPTIONS[Number(e.target.value)])}
          className="flex-1 accent-kolt-orange" aria-label="Quantity slider" data-testid="box-qty-slider" />
        <div ref={wrap} className="relative">
          <div className="flex border border-slate-300 bg-white focus-within:border-kolt-orange focus-within:ring-1 focus-within:ring-kolt-orange">
            <input type="text" inputMode="numeric" value={text} aria-label="Quantity"
              onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ""))}
              onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { commit(); setOpen(false); } }}
              onFocus={() => setOpen(true)}
              className="w-20 px-3 py-2.5 text-lg font-mono text-kolt-slate outline-none" data-testid="box-qty-input" />
            <button type="button" onClick={() => setOpen((v) => !v)} aria-label="Choose quantity" data-testid="box-qty-dropdown-btn"
              className="px-2 text-slate-500 hover:text-kolt-orange border-l border-slate-200">
              <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </div>
          {open && (
            <ul ref={listRef} role="listbox" data-testid="box-qty-list"
              className="absolute z-20 right-0 mt-1 w-32 max-h-56 overflow-y-auto bg-white border border-slate-300 shadow-lg">
              {QTY_OPTIONS.map((q) => (
                <li key={q} role="option" aria-selected={q === qty} data-active={q === qty} data-testid={`box-qty-option-${q}`}
                  onMouseDown={() => { setQty(q); setOpen(false); }}
                  className={`px-3 py-2 text-sm font-mono cursor-pointer ${q === qty ? "bg-kolt-orange text-white" : "hover:bg-slate-100 text-kolt-slate"}`}>
                  {q}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-2" data-testid="box-qty-hint">
        Order 1 as a printed proof, or any multiple of 12 up to {MAX_QTY}.{" "}
        <Link to={quoteHref} className="text-kolt-orange font-semibold hover:underline" data-testid="box-qty-quote-link">
          Need more than {MAX_QTY}? Request a custom quote →
        </Link>
      </p>
    </div>
  );
}
